"use client";

import { useEffect } from "react";
import posthog from "posthog-js";
import {
  readAdvertisingConsent,
  readAnalyticsConsent,
} from "@/components/analytics/browser-attribution";
import { subscribeToBrowserConsent } from "@/components/analytics/browser-consent-lifecycle";
import { POSTHOG_BROWSER_INITIALIZED_EVENT } from "@/lib/analytics/posthog-browser-lifecycle";
import type { ComparePage } from "@/lib/compare/schema";
import {
  trackCompareEvent,
  clickAttrToEventName,
  incrementComparePagesViewedCount,
  type CompareClickEventName,
} from "@/lib/compare/analytics";

/**
 * Phase 49 — the ONE client island per compare page.
 *
 * Sibling of <CompareJsonLd> inside <ComparePageShell>. The shell and
 * every block stay server components; this component is the only
 * `"use client"` boundary on any compare page.
 *
 * Responsibilities (see 49-CONTEXT.md):
 *   - Delegated click listener on the document reads
 *     `data-compare-event` from `closest("a[data-compare-event]")` and
 *     fires one of `compare_pricing_cta_clicked`,
 *     `compare_start_free_cta_clicked`, or `compare_related_link_clicked`.
 *   - IntersectionObserver at threshold 0.5 watches every
 *     `data-testid="compare-*"` section and fires
 *     `compare_section_viewed` (or `compare_faq_section_viewed` for
 *     the FAQ) at most once per page load.
 *   - Sets the first-party `tsb_compare_src` cookie (7-day TTL,
 *     SameSite=Lax) so the downstream <CompareAssistedSignupTracker>
 *     can attribute a signup back to the compare page that drove it.
 *   - Increments the `compare_pages_viewed_count` person property
 *     (secondary signal).
 *
 * Consent: attribution and analytics are activated only after the
 * authoritative consent cookie is accepted and PostHog has initialised.
 * Withdrawal disconnects the instrumentation and deletes the optional
 * attribution cookie immediately.
 */

interface CompareTrackerProps {
  page: Pick<ComparePage, "slug" | "kind" | "reviewedAt">;
}

const COMPARE_SECTION_TESTIDS = [
  "compare-hero",
  "compare-snapshot-table",
  "compare-narrative",
  "compare-best-for",
  "compare-bottom-line",
  "compare-why-switch",
  "compare-product-breakdown-section",
  "compare-internal-links",
  "compare-faq",
  "compare-evidence-footnote",
] as const;

/**
 * Click attributes this tracker owns.
 *
 * `help_link` is deliberately NOT here (#1857): the Help evidence module ships
 * its own <CompareHelpLinkTracker>, because two of the four surfaces that
 * render it are ad landings with no <CompareTracker> mounted. Adding it here
 * would double-capture on /compare/* detail pages, which mount both.
 */
const CLICK_ATTR_VALUES: ReadonlySet<CompareClickEventName> =
  new Set<CompareClickEventName>([
    "pricing_cta",
    "start_free_cta",
    "related_link",
    "source_link",
    "business_plan",
    "site_control",
    "handshq_comparison",
    "contact_sales",
  ]);

function setCompareSourceCookie(slug: string): void {
  if (typeof document === "undefined") return;
  const SEVEN_DAYS_MS = 7 * 864e5;
  const expires = new Date(Date.now() + SEVEN_DAYS_MS).toUTCString();
  document.cookie = `tsb_compare_src=${encodeURIComponent(slug)}; expires=${expires}; path=/; SameSite=Lax`;
}

function clearCompareSourceCookie(): void {
  if (typeof document === "undefined") return;
  document.cookie =
    "tsb_compare_src=; Max-Age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax";
}

function extractRelatedTargetSlug(href: string): string | null {
  // href is typically "/compare/thesitebook-vs-handshq" — last segment is the slug.
  try {
    const url = new URL(href, "http://placeholder.local");
    const match = url.pathname.match(/^\/compare\/([a-z0-9-]+)\/?$/);
    return match?.[1] ?? null;
  } catch {
    return null;
  }
}

export function CompareTracker({ page }: CompareTrackerProps) {
  useEffect(() => {
    let deactivateTracking: (() => void) | null = null;

    function activateTracking() {
      if (
        deactivateTracking ||
        readAnalyticsConsent() !== "accepted" ||
        !posthog.__loaded
      ) {
        return;
      }

      incrementComparePagesViewedCount();

      function onClick(e: MouseEvent) {
        if (readAnalyticsConsent() !== "accepted") return;
        const target = e.target;
        if (!(target instanceof Element)) return;
        const anchor = target.closest<HTMLAnchorElement>(
          "a[data-compare-event]",
        );
        if (!anchor) return;

        const attr = anchor.getAttribute("data-compare-event");
        if (!attr || !CLICK_ATTR_VALUES.has(attr as CompareClickEventName)) {
          return;
        }
        const attrName = attr as CompareClickEventName;
        const eventName = clickAttrToEventName(attrName);

        if (eventName === "compare_related_link_clicked") {
          const targetSlug = extractRelatedTargetSlug(
            anchor.getAttribute("href") ?? "",
          );
          if (!targetSlug) return;
          trackCompareEvent(
            { event: "compare_related_link_clicked", target_slug: targetSlug },
            page,
          );
          return;
        }

        if (eventName === "compare_pricing_cta_clicked") {
          trackCompareEvent(
            {
              event: "compare_pricing_cta_clicked",
              position:
                anchor.getAttribute("data-compare-position") ?? undefined,
            },
            page,
          );
          return;
        }

        if (eventName === "compare_start_free_cta_clicked") {
          trackCompareEvent(
            {
              event: "compare_start_free_cta_clicked",
              position:
                anchor.getAttribute("data-compare-position") ?? undefined,
            },
            page,
          );
          return;
        }

        if (eventName === "compare_source_link_clicked") {
          trackCompareEvent(
            {
              event: "compare_source_link_clicked",
              target_url: anchor.href,
              source_product:
                anchor.getAttribute("data-compare-source-product") ?? undefined,
            },
            page,
          );
          return;
        }

        const position =
          anchor.getAttribute("data-compare-position") ?? undefined;
        if (eventName === "compare_business_plan_clicked") {
          trackCompareEvent({ event: eventName, position }, page);
        } else if (eventName === "compare_site_control_clicked") {
          trackCompareEvent({ event: eventName, position }, page);
        } else if (eventName === "compare_handshq_comparison_clicked") {
          trackCompareEvent({ event: eventName, position }, page);
        } else if (eventName === "compare_contact_sales_clicked") {
          trackCompareEvent({ event: eventName, position }, page);
        }
      }

      document.addEventListener("click", onClick);

      const firedSections = new Set<string>();
      const observer = new IntersectionObserver(
        (entries) => {
          if (readAnalyticsConsent() !== "accepted") return;
          for (const entry of entries) {
            if (!entry.isIntersecting || entry.intersectionRatio < 0.5) {
              continue;
            }
            const el = entry.target;
            const testid = el.getAttribute("data-testid");
            if (!testid || firedSections.has(testid)) continue;
            firedSections.add(testid);

            if (testid === "compare-faq") {
              trackCompareEvent(
                { event: "compare_faq_section_viewed", section_id: testid },
                page,
              );
            } else {
              trackCompareEvent(
                { event: "compare_section_viewed", section_id: testid },
                page,
              );
            }
            observer.unobserve(el);
          }
        },
        { threshold: 0.5 },
      );

      for (const testid of COMPARE_SECTION_TESTIDS) {
        const el = document.querySelector(`[data-testid="${testid}"]`);
        if (el) observer.observe(el);
      }

      deactivateTracking = () => {
        document.removeEventListener("click", onClick);
        observer.disconnect();
      };
    }

    function deactivateAnalytics() {
      deactivateTracking?.();
      deactivateTracking = null;
    }

    function reconcileTracking() {
      if (readAdvertisingConsent() === "accepted") {
        setCompareSourceCookie(page.slug);
      } else {
        clearCompareSourceCookie();
      }
      if (readAnalyticsConsent() === "accepted") {
        activateTracking();
      } else {
        deactivateAnalytics();
      }
    }

    reconcileTracking();
    window.addEventListener(
      POSTHOG_BROWSER_INITIALIZED_EVENT,
      reconcileTracking,
    );
    const unsubscribeConsent = subscribeToBrowserConsent(reconcileTracking);

    return () => {
      deactivateTracking?.();
      window.removeEventListener(
        POSTHOG_BROWSER_INITIALIZED_EVENT,
        reconcileTracking,
      );
      unsubscribeConsent();
    };
  }, [page]);

  return null;
}
